import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Filter, Search, Upload, Download, RefreshCw, Loader2,
  CheckCircle2, XCircle, Send, Mail, Phone, MapPin, Globe, ExternalLink,
  Sparkles, Tag, AlertTriangle, ChevronDown, X as XIcon, Zap, Compass,
  Eye, Link2, Instagram, Facebook, Star, MapPinned, Flame, ShieldAlert, Copy, Clock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Stage configuration — colour + label for the kanban / chips
const STAGES = [
  { key: 'discovered',          label: 'Needs Review',  color: '#9CA3AF', bg: '#F3F4F6' },
  { key: 'enriched',            label: 'Enriched',      color: '#6B7280', bg: '#F3F4F6' },
  { key: 'approved',            label: 'Approved',      color: '#0F0F0F', bg: '#FFEDE8' },
  { key: 'contacted',           label: 'Contacted',     color: '#1F2937', bg: '#F3F4F6' },
  { key: 'responded',           label: 'Responded',     color: '#FF2E00', bg: '#FFEDE8' },
  { key: 'discovery_scheduled', label: 'Discovery',     color: '#1F2937', bg: '#F3F4F6' },
  { key: 'demo_booked',         label: 'Demo Booked',   color: '#1F2937', bg: '#F3F4F6' },
  { key: 'demo_completed',      label: 'Demo Done',     color: '#1F2937', bg: '#F3F4F6' },
  { key: 'proposal_sent',       label: 'Proposal Sent', color: '#1F2937', bg: '#F3F4F6' },
  { key: 'verbal_yes',          label: 'Verbal Yes',    color: '#FF2E00', bg: '#FFEDE8' },
  { key: 'closed_won',          label: 'Closed Won',    color: '#10B981', bg: '#E7F8F0' },
  { key: 'lost',                label: 'Lost',          color: '#9CA3AF', bg: '#F9FAFB' },
  { key: 'do_not_contact',      label: 'DNC',           color: '#9CA3AF', bg: '#F9FAFB' },
];

const CATEGORIES = [
  'strength', 'powerlifting', 'weightlifting', 'strongman',
  'crossfit', 'boxing', 'mma', 'martial_arts',
  'boutique', 'personal_training', 'performance', 'independent',
];

const TAGS = [
  'no_website', 'weak_website', 'outdated_site', 'no_lead_form',
  'no_online_signup', 'no_pricing_page', 'no_clear_cta',
  'instagram_only', 'facebook_only', 'strong_community_weak_funnel',
  'good_fit', 'needs_manual_review',
];

const tagLabel = (t) => t.replace(/_/g, ' ');

async function authFetch(path, opts = {}) {
  const token = localStorage.getItem('scs_token') || localStorage.getItem('huscleos_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) };
  const res = await fetch(`${API}${path}`, { ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `${res.status} ${res.statusText}`);
  }
  return res.json();
}

/* ── KPI Strip ─────────────────────────────────────────────── */
function StatCard({ label, value, accent = '#0F0F0F', testId }) {
  return (
    <div className="bg-white border border-gray-200 px-4 py-3" data-testid={testId}>
      <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1" style={{ fontFamily: 'Manrope' }}>{label}</p>
      <p className="text-2xl font-bold tabular-nums leading-none" style={{ fontFamily: 'Manrope', color: accent }}>{value}</p>
    </div>
  );
}

/* ── Add / Edit Prospect Modal ───────────────────────────────── */
function ProspectFormModal({ prospect, onClose, onSaved }) {
  const isEdit = !!prospect?.id;
  const [f, setF] = useState({
    gym_name: prospect?.gym_name || '',
    category: prospect?.category || 'independent',
    address: prospect?.address || '',
    city: prospect?.city || '',
    state: prospect?.state || '',
    zip_code: prospect?.zip_code || '',
    phone: prospect?.phone || '',
    email: prospect?.email || '',
    website: prospect?.website || '',
    website_status: prospect?.website_status || 'website_needs_review',
    instagram_url: prospect?.instagram_url || '',
    facebook_url: prospect?.facebook_url || '',
    tiktok_url: prospect?.tiktok_url || '',
    google_business_url: prospect?.google_business_url || '',
    google_rating: prospect?.google_rating ?? '',
    review_count: prospect?.review_count ?? '',
    owner_name: prospect?.owner_name || '',
    contact_name: prospect?.contact_name || '',
    contact_title: prospect?.contact_title || '',
    notes: prospect?.notes || '',
    source_url: prospect?.source_url || '',
    opportunity_tags: prospect?.opportunity_tags || [],
  });
  const [saving, setSaving] = useState(false);

  const toggleTag = (t) => setF((p) => ({
    ...p,
    opportunity_tags: p.opportunity_tags.includes(t)
      ? p.opportunity_tags.filter((x) => x !== t)
      : [...p.opportunity_tags, t],
  }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.gym_name.trim()) { toast.error('Gym name is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        google_rating: f.google_rating === '' ? null : Number(f.google_rating),
        review_count: f.review_count === '' ? null : parseInt(f.review_count, 10),
      };
      let data;
      if (isEdit) {
        data = await authFetch(`/api/staff/prospects/${prospect.id}`, { method: 'PATCH', body: JSON.stringify(payload) });
        toast.success('Prospect updated');
      } else {
        const res = await authFetch('/api/staff/prospects', { method: 'POST', body: JSON.stringify(payload) });
        if (res.status === 'duplicate') {
          toast.error(`Duplicate of ${res.existing.gym_name} (already in pipeline)`);
          setSaving(false);
          return;
        }
        data = res.prospect;
        toast.success('Prospect added');
      }
      onSaved(data);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="prospect-form-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-3xl bg-white shadow-2xl my-auto" style={{ fontFamily: 'Manrope' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.5px', color: '#0F0F0F' }}>
            {isEdit ? `EDIT — ${prospect.gym_name}` : 'ADD PROSPECT'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1" data-testid="prospect-form-close"><XIcon size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Field label="Gym name *" value={f.gym_name} onChange={(v) => setF({ ...f, gym_name: v })} testId="form-gym-name" required />
            <Field label="Category" type="select" options={CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') }))} value={f.category} onChange={(v) => setF({ ...f, category: v })} testId="form-category" />
            <Field label="Owner name" value={f.owner_name} onChange={(v) => setF({ ...f, owner_name: v })} testId="form-owner" />
            <Field label="Contact name" value={f.contact_name} onChange={(v) => setF({ ...f, contact_name: v })} testId="form-contact" />
            <Field label="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} type="email" testId="form-email" />
            <Field label="Phone" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="form-phone" />
            <Field label="Website" value={f.website} onChange={(v) => setF({ ...f, website: v })} testId="form-website" />
            <Field label="Website status" type="select" options={[
              { value: 'no_website_listed', label: 'No website listed' },
              { value: 'active', label: 'Active' },
              { value: 'broken', label: 'Broken' },
              { value: 'outdated', label: 'Outdated' },
              { value: 'website_needs_review', label: 'Needs review' },
            ]} value={f.website_status} onChange={(v) => setF({ ...f, website_status: v })} testId="form-website-status" />
            <Field label="City" value={f.city} onChange={(v) => setF({ ...f, city: v })} testId="form-city" />
            <Field label="State" value={f.state} onChange={(v) => setF({ ...f, state: v })} testId="form-state" />
            <Field label="ZIP" value={f.zip_code} onChange={(v) => setF({ ...f, zip_code: v })} testId="form-zip" />
            <Field label="Google rating" value={f.google_rating} onChange={(v) => setF({ ...f, google_rating: v })} type="number" step="0.1" testId="form-rating" />
            <Field label="Review count" value={f.review_count} onChange={(v) => setF({ ...f, review_count: v })} type="number" testId="form-reviews" />
            <Field label="Source URL" value={f.source_url} onChange={(v) => setF({ ...f, source_url: v })} testId="form-source" />
            <Field label="Instagram URL" value={f.instagram_url} onChange={(v) => setF({ ...f, instagram_url: v })} testId="form-instagram" />
            <Field label="Facebook URL" value={f.facebook_url} onChange={(v) => setF({ ...f, facebook_url: v })} testId="form-facebook" />
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Opportunity tags</label>
            <div className="flex flex-wrap gap-1.5">
              {TAGS.map((t) => (
                <button key={t} type="button" onClick={() => toggleTag(t)}
                  className={`text-[11px] font-semibold tracking-wide uppercase px-2.5 py-1 border transition-colors ${
                    f.opportunity_tags.includes(t)
                      ? 'bg-[#FF2E00] border-[#FF2E00] text-white'
                      : 'border-gray-200 text-gray-700 hover:border-gray-900'
                  }`}
                  data-testid={`form-tag-${t}`}>
                  {tagLabel(t)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Notes</label>
            <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={3}
              className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900"
              data-testid="form-notes" />
          </div>
          <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2.5 text-xs font-bold tracking-widest uppercase border border-gray-200 text-gray-700 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={saving} className="px-6 py-2.5 text-xs font-bold tracking-widest uppercase bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50" data-testid="form-submit">
              {saving ? 'SAVING…' : (isEdit ? 'SAVE CHANGES' : 'ADD PROSPECT')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', step, options, required, testId }) {
  if (type === 'select') {
    return (
      <div>
        <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">{label}</label>
        <select value={value} onChange={(e) => onChange(e.target.value)}
          className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900"
          data-testid={testId}>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">{label}</label>
      <input type={type} step={step} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 focus:outline-none focus:border-gray-900"
        data-testid={testId} />
    </div>
  );
}

/* ── Prospect Detail Drawer (right-side) ─────────────────────── */
function ProspectDetail({ prospect, onClose, onChanged, sequences }) {
  const [tab, setTab] = useState('overview');
  const [seqKey, setSeqKey] = useState(sequences?.[0]?.key || 'no_website');
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [preview, setPreview] = useState(null);

  const setStage = async (stage) => {
    try {
      await authFetch(`/api/staff/prospects/${prospect.id}`, { method: 'PATCH', body: JSON.stringify({ stage }) });
      toast.success(`Stage → ${STAGES.find((s) => s.key === stage)?.label}`);
      onChanged();
    } catch (err) { toast.error(err.message); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/scan`, { method: 'POST' });
      toast.success(`Scanned · ${r.scan?.status || 'done'}${r.scan?.load_time_ms != null ? ` · ${r.scan.load_time_ms}ms` : ''}`);
      onChanged();
    } catch (err) { toast.error(err.message || 'Scan failed'); }
    finally { setScanning(false); }
  };

  const runEnrich = async () => {
    setEnriching(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/enrich`, { method: 'POST' });
      const igStatus = r.instagram?.status || '—';
      const mapsStatus = r.google_maps?.status || '—';
      toast.success(`Enriched · IG: ${igStatus} · Maps: ${mapsStatus}`);
      onChanged();
    } catch (err) { toast.error(err.message || 'Enrich failed'); }
    finally { setEnriching(false); }
  };

  const [cloning, setCloning] = useState(false);
  const cloneToDemo = async () => {
    if (!window.confirm(`Build a full sandboxed HuscleOS demo for ${prospect.gym_name}? This generates a demo CRM, 20 sample leads, and a login you can share with them.`)) return;
    setCloning(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/clone-to-demo`, { method: 'POST' });
      const demo = r.demo_account || {};
      toast.success(`Demo built — login: ${demo.email || '—'}`);
      navigator.clipboard?.writeText(`Login: ${demo.email}\nPassword: ${demo.password}\nURL: /staff/login`).catch(() => {});
      onChanged();
    } catch (err) { toast.error(err.message || 'Clone failed'); }
    finally { setCloning(false); }
  };

  const [publishing, setPublishing] = useState(false);
  const publishPublicDemo = async (regenerate = false) => {
    setPublishing(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/publish-demo`, {
        method: 'POST', body: JSON.stringify({ regenerate_insights: regenerate }),
      });
      const url = r.public_url_slug || r.public_url_token;
      if (url) {
        navigator.clipboard?.writeText(url).catch(() => {});
        toast.success(`Public demo ready — URL copied`);
      } else {
        toast.success('Public demo published');
      }
      onChanged();
    } catch (err) { toast.error(err.message || 'Publish failed'); }
    finally { setPublishing(false); }
  };

  const [detectingConflicts, setDetectingConflicts] = useState(false);
  const detectConflicts = async () => {
    setDetectingConflicts(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/detect-conflicts`, { method: 'POST' });
      toast.success(`${r.count} conflict${r.count === 1 ? '' : 's'} flagged`);
      onChanged();
    } catch (err) { toast.error(err.message || 'Detection failed'); }
    finally { setDetectingConflicts(false); }
  };

  const [scoring, setScoring] = useState(false);
  const aiScore = async () => {
    setScoring(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/ai-score`, { method: 'POST' });
      toast.success(`AI score: ${r.score}/100 — ${r.next_action}`);
      onChanged();
    } catch (err) { toast.error(err.message || 'Scoring failed'); }
    finally { setScoring(false); }
  };

  const [drafting, setDrafting] = useState(false);
  const [draftTone, setDraftTone] = useState('direct');
  const generateOutreach = async (tone = draftTone) => {
    setDrafting(true);
    try {
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/generate-outreach`, {
        method: 'POST', body: JSON.stringify({ tone }),
      });
      toast.success(`Drafts ready (${tone} tone)`);
      onChanged();
    } catch (err) { toast.error(err.message || 'Generation failed'); }
    finally { setDrafting(false); }
  };

  const sendOutreach = async () => {
    setSending(true);
    try {
      const data = await authFetch(`/api/staff/prospects/${prospect.id}/send-outreach`,
        { method: 'POST', body: JSON.stringify({ sequence_key: seqKey, step }) });
      toast.success(`Sent · ${data.sent_today}/${data.daily_cap} today`);
      setPreview(data.log);
      onChanged();
    } catch (err) { toast.error(err.message || 'Send failed'); }
    finally { setSending(false); }
  };

  const logReply = async (kind) => {
    try {
      await authFetch(`/api/staff/prospects/${prospect.id}/log-reply`,
        { method: 'POST', body: JSON.stringify({ kind }) });
      toast.success(kind === 'opt_out' ? 'Marked Do Not Contact' : kind === 'negative' ? 'Marked Lost' : 'Marked Responded');
      onChanged();
    } catch (err) { toast.error(err.message); }
  };

  const stage = STAGES.find((s) => s.key === prospect.stage) || STAGES[0];

  return (
    <div className="fixed inset-0 z-[55] flex" onClick={onClose} data-testid="prospect-detail">
      <div className="flex-1 bg-black/40" />
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" style={{ fontFamily: 'Manrope' }}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold tracking-tight leading-tight" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.5px', color: '#0F0F0F' }}>
              {prospect.gym_name?.toUpperCase()}
            </h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5" style={{ background: stage.bg, color: stage.color }}>
                {stage.label}
              </span>
              <span className="text-xs text-gray-500">Fit score <strong className="text-gray-900">{prospect.fit_score}</strong>/100</span>
              {prospect.opportunity_tags?.slice(0, 3).map((t) => (
                <span key={t} className="text-[10px] font-semibold tracking-wide uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600">{tagLabel(t)}</span>
              ))}
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1" data-testid="prospect-detail-close"><XIcon size={20} /></button>
        </div>

        <div className="border-b border-gray-200 flex">
          {['overview', 'outreach', 'proposal', 'activity'].map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-3 text-xs font-bold tracking-widest uppercase border-b-2 transition-colors ${
                tab === t ? 'border-[#FF2E00] text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-900'
              }`} data-testid={`tab-${t}`}>{t}</button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="p-6 space-y-5">
            <Row icon={<Mail size={14} />} value={prospect.email || '—'} />
            <Row icon={<Phone size={14} />} value={prospect.phone || '—'} />
            <Row icon={<Globe size={14} />} value={prospect.website ? <a href={prospect.website} target="_blank" rel="noreferrer" className="text-[#FF2E00] hover:underline">{prospect.website} <ExternalLink size={11} className="inline" /></a> : <span className="text-gray-500">no website</span>} />
            <Row icon={<MapPin size={14} />} value={[prospect.address, prospect.city, prospect.state, prospect.zip_code].filter(Boolean).join(', ') || '—'} />
            {prospect.google_rating && <Row icon={<Sparkles size={14} />} value={`${prospect.google_rating}★ · ${prospect.review_count || 0} Google reviews`} />}
            {(prospect.instagram_url || prospect.instagram_followers != null) && (
              <Row icon={<Sparkles size={14} />} value={
                <span>
                  {prospect.instagram_url && <a href={prospect.instagram_url} target="_blank" rel="noreferrer" className="text-[#FF2E00] hover:underline">IG <ExternalLink size={10} className="inline" /></a>}
                  {prospect.instagram_followers != null && <span className="ml-2 text-gray-700">{prospect.instagram_followers.toLocaleString()} followers</span>}
                  {prospect.instagram_posts != null && <span className="ml-2 text-gray-500">{prospect.instagram_posts} posts</span>}
                </span>
              } />
            )}
            {prospect.website_scan && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-gray-50 border border-gray-200" data-testid="scan-signals">
                <ScanStat label="STATUS" value={prospect.website_scan.status || '—'} ok={prospect.website_scan.status === 'ok'} />
                <ScanStat label="LOAD" value={prospect.website_scan.load_time_ms != null ? `${prospect.website_scan.load_time_ms}ms` : '—'} ok={prospect.website_scan.load_time_ms != null && prospect.website_scan.load_time_ms < 3000} warn={prospect.website_scan.slow_site} />
                <ScanStat label="SSL" value={prospect.website_scan.ssl_valid === true ? 'YES' : prospect.website_scan.ssl_valid === false ? 'NO' : '—'} ok={prospect.website_scan.ssl_valid === true} warn={prospect.website_scan.ssl_valid === false} />
                <ScanStat label="MOBILE" value={prospect.website_scan.mobile_friendly ? 'YES' : 'NO'} ok={prospect.website_scan.mobile_friendly} warn={!prospect.website_scan.mobile_friendly && prospect.website_scan.status === 'ok'} />
              </div>
            )}
            {prospect.notes && <div className="p-3 bg-gray-50 border border-gray-200 text-sm text-gray-700">{prospect.notes}</div>}

            <div className="flex flex-wrap gap-2">
              <button onClick={runScan} disabled={scanning} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-scan">
                {scanning ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />} Scan website
              </button>
              <button onClick={runEnrich} disabled={enriching} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-enrich">
                {enriching ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} Enrich (IG + Maps)
              </button>
              <button onClick={cloneToDemo} disabled={cloning} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-[#FF2E00] text-[#FF2E00] hover:bg-[#FF2E00] hover:text-white disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-clone-demo" title="Build a full HuscleOS demo CRM for this prospect">
                {cloning ? <Loader2 size={11} className="animate-spin" /> : <Compass size={11} />} {prospect.demo_lead_id ? 'Demo built' : 'Build their demo'}
              </button>
              <button onClick={() => publishPublicDemo(false)} disabled={publishing} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-900 bg-gray-900 text-white hover:bg-black disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-publish-demo" title="Generate a public landing page personalized for this prospect">
                {publishing ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />} {prospect.demo_published_at ? 'Refresh public page' : 'Publish public page'}
              </button>
              <button onClick={detectConflicts} disabled={detectingConflicts} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-detect-conflicts" title="Re-pull Google + Yelp and compare for pitch-ready conflicts">
                {detectingConflicts ? <Loader2 size={11} className="animate-spin" /> : <ShieldAlert size={11} />} {(prospect.conflicts_count || 0) > 0 ? `Re-check (${prospect.conflicts_count})` : 'Detect conflicts'}
              </button>
              <button onClick={aiScore} disabled={scoring} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-ai-score" title="LLM scores 0-100 likelihood-to-close + next action">
                {scoring ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />} {prospect.ai_score != null ? `AI ${prospect.ai_score}/100` : 'AI score'}
              </button>
              <button onClick={() => generateOutreach('direct')} disabled={drafting} className="text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 inline-flex items-center gap-1.5" data-testid="prospect-generate-outreach" title="LLM drafts email + LinkedIn + SMS from this prospect's conflicts">
                {drafting ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} {prospect.outreach_drafts ? 'Re-draft' : 'Draft outreach'}
              </button>
            </div>

            {prospect.ai_score != null && (
              <AIScoreCard prospect={prospect} onRescore={aiScore} rescoring={scoring} />
            )}

            {(prospect.conflicts || []).length > 0 && (
              <ConflictsPanel prospect={prospect} />
            )}

            {prospect.outreach_drafts && (
              <OutreachDraftsPanel prospect={prospect} draftTone={draftTone} setDraftTone={setDraftTone} onRegenerate={generateOutreach} drafting={drafting} />
            )}

            {prospect.demo_published_at && (
              <PublicDemoPanel prospect={prospect} onRegenerate={() => publishPublicDemo(true)} regenerating={publishing} />
            )}

            <div>
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Move stage</p>
              <div className="flex flex-wrap gap-1">
                {STAGES.map((s) => (
                  <button key={s.key} onClick={() => setStage(s.key)}
                    className={`text-[10px] font-bold tracking-wide uppercase px-2 py-1 border transition-colors ${
                      prospect.stage === s.key ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-900'
                    }`} data-testid={`stage-btn-${s.key}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
              <button onClick={() => logReply('positive')} className="text-[10px] font-bold tracking-widest uppercase py-2 border border-[#FF2E00] text-[#FF2E00] hover:bg-[#FF2E00] hover:text-white transition-colors" data-testid="reply-positive">+ Reply</button>
              <button onClick={() => logReply('negative')} className="text-[10px] font-bold tracking-widest uppercase py-2 border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors" data-testid="reply-negative">Lost</button>
              <button onClick={() => logReply('opt_out')} className="text-[10px] font-bold tracking-widest uppercase py-2 border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors" data-testid="reply-optout">DNC</button>
            </div>
          </div>
        )}

        {tab === 'outreach' && (
          <div className="p-6 space-y-4">
            {(prospect.stage === 'discovered' || prospect.stage === 'enriched') && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
                <AlertTriangle size={14} /> <span>Approve the prospect first before sending outreach.</span>
              </div>
            )}
            {(prospect.email_opt_out || prospect.stage === 'do_not_contact') && (
              <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs">
                <AlertTriangle size={14} /> <span>This prospect is on Do Not Contact.</span>
              </div>
            )}
            {!prospect.email && (
              <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
                <AlertTriangle size={14} /> <span>No email on file. Add one in the Edit form first.</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2">
              <Field label="Sequence" type="select" value={seqKey} onChange={setSeqKey}
                options={(sequences || []).map((s) => ({ value: s.key, label: s.name }))} testId="seq-select" />
              <Field label="Step" type="select" value={String(step)} onChange={(v) => setStep(Number(v))}
                options={[1, 2, 3].map((n) => ({ value: String(n), label: `Step ${n}` }))} testId="seq-step" />
            </div>

            {(() => {
              const seq = sequences?.find((s) => s.key === seqKey);
              const stp = seq?.steps?.find((x) => x.step === step);
              if (!stp) return null;
              return (
                <div className="bg-gray-50 border border-gray-200 p-4 space-y-2">
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Subject</p>
                  <p className="text-sm font-bold text-gray-900">{stp.subject}</p>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mt-3">Body</p>
                  <pre className="text-xs whitespace-pre-wrap text-gray-700 font-sans leading-relaxed">{stp.body}</pre>
                </div>
              );
            })()}

            <button onClick={sendOutreach} disabled={sending || !prospect.email || prospect.stage === 'do_not_contact' || prospect.email_opt_out || prospect.stage === 'discovered' || prospect.stage === 'enriched'}
              className="w-full py-3 text-xs font-bold tracking-widest uppercase bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="send-outreach">
              {sending ? <><Loader2 size={14} className="animate-spin" /> SENDING…</> : <><Send size={13} /> SEND TO {prospect.email || '—'}</>}
            </button>

            {preview && (
              <div className="bg-green-50 border border-green-200 p-3 text-xs text-green-800" data-testid="send-preview">
                Sent at {new Date(preview.sent_at).toLocaleTimeString()}
              </div>
            )}

            {prospect.outreach && prospect.outreach.length > 0 && (
              <div className="pt-3 border-t border-gray-200">
                <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Sent so far</p>
                <div className="space-y-2">
                  {prospect.outreach.map((o) => (
                    <div key={o.id} className="text-xs bg-white border border-gray-200 px-3 py-2">
                      <p className="font-bold text-gray-900">{o.subject}</p>
                      <p className="text-gray-500">{new Date(o.sent_at).toLocaleString()} · {o.sequence_key || 'custom'} step {o.step}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {tab === 'proposal' && <ProposalTab prospect={prospect} onSaved={onChanged} />}

        {tab === 'activity' && (
          <div className="p-6 space-y-2">
            {(prospect.activity_log || []).slice().reverse().map((a, i) => (
              <div key={i} className="text-xs border-l-2 border-gray-200 pl-3 py-1.5">
                <p className="font-bold text-gray-900">{a.note}</p>
                <p className="text-gray-500">{a.staff_name || 'system'} · {new Date(a.timestamp).toLocaleString()}</p>
              </div>
            ))}
            {(!prospect.activity_log || prospect.activity_log.length === 0) && (
              <p className="text-xs text-gray-500">No activity yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon, value }) {
  return (
    <div className="flex items-center gap-3 text-sm text-gray-700">
      <span className="text-gray-400 shrink-0">{icon}</span>
      <span className="truncate">{value}</span>
    </div>
  );
}

/** Tiny icon strip with clickable links to a prospect's website, Google, IG, FB, TikTok, Yelp. */
function SocialIcons({ prospect, size = 11, className = '' }) {
  const stop = (e) => e.stopPropagation();
  const items = [];
  if (prospect.website && !(prospect.website || '').includes('yelp.com')) items.push({ key: 'web', href: prospect.website, label: 'Website', Icon: Globe, color: '#0F0F0F' });
  if (prospect.place_url || prospect.google_maps_url || prospect.google_business_url) items.push({
    key: 'gmaps',
    href: prospect.place_url || prospect.google_maps_url || prospect.google_business_url,
    label: prospect.google_rating ? `Google ${prospect.google_rating}★` : 'Google',
    Icon: MapPinned, color: '#4285F4',
  });
  if (prospect.yelp_url) items.push({
    key: 'yelp',
    href: prospect.yelp_url,
    label: prospect.yelp_rating ? `Yelp ${prospect.yelp_rating}★` : 'Yelp',
    Icon: Flame, color: '#D32323',
  });
  if (prospect.instagram_url) items.push({ key: 'ig', href: prospect.instagram_url, label: 'Instagram', Icon: Instagram, color: '#E1306C' });
  if (prospect.facebook_url) items.push({ key: 'fb', href: prospect.facebook_url, label: 'Facebook', Icon: Facebook, color: '#1877F2' });
  if (prospect.tiktok_url) items.push({ key: 'tt', href: prospect.tiktok_url, label: 'TikTok', Icon: ExternalLink, color: '#0F0F0F' });
  if (items.length === 0) return null;
  return (
    <div className={`flex items-center gap-1.5 ${className}`} onClick={stop} data-testid={`socials-${prospect.id || 'row'}`}>
      {items.map(({ key, href, label, Icon, color }) => (
        <a key={key} href={href} target="_blank" rel="noreferrer" title={label}
           onClick={stop}
           className="inline-flex items-center justify-center w-5 h-5 border border-gray-200 hover:border-gray-900 transition-colors"
           style={{ color }}
           data-testid={`social-${key}-${prospect.id || 'row'}`}>
          <Icon size={size} />
        </a>
      ))}
      {prospect.google_rating != null && (
        <span className="text-[10px] font-bold tracking-wide text-gray-600 inline-flex items-center gap-0.5">
          <Star size={9} className="text-yellow-500 fill-yellow-500" />{prospect.google_rating}
          {prospect.review_count != null && <span className="text-gray-400">·{prospect.review_count}</span>}
        </span>
      )}
    </div>
  );
}

function ScanStat({ label, value, ok, warn }) {
  const color = ok ? 'text-green-700' : warn ? 'text-red-600' : 'text-gray-700';
  const dot = ok ? 'bg-green-500' : warn ? 'bg-red-500' : 'bg-gray-300';
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[9px] font-bold tracking-widest uppercase text-gray-500 flex items-center gap-1">
        <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} /> {label}
      </span>
      <span className={`text-sm font-bold ${color}`}>{value}</span>
    </div>
  );
}

function AIScoreCard({ prospect, onRescore, rescoring }) {
  const score = prospect.ai_score || 0;
  const color = score >= 80 ? '#10B981' : score >= 60 ? '#F59E0B' : score >= 40 ? '#6B7280' : '#9CA3AF';
  const label = score >= 80 ? 'HOT' : score >= 60 ? 'WARM' : score >= 40 ? 'COLD' : 'ICE';
  const scoredAt = prospect.ai_score_at ? new Date(prospect.ai_score_at) : null;
  return (
    <div className="p-3 border border-blue-100 bg-blue-50/40" data-testid="ai-score-card">
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-16 text-center">
          <div className="text-3xl font-black tabular-nums leading-none" style={{ color, fontFamily: 'Manrope' }}>{score}</div>
          <div className="text-[9px] font-bold tracking-widest uppercase mt-1" style={{ color }}>{label}</div>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-widest uppercase text-blue-700 mb-1">AI LIKELIHOOD-TO-CLOSE · 30d</p>
          <p className="text-xs text-gray-700 leading-snug mb-2">{prospect.ai_score_reasoning || '—'}</p>
          {prospect.ai_score_next_action && (
            <p className="text-[11px] text-blue-900 inline-flex items-center gap-1.5"><Zap size={11} className="text-blue-500" /> <strong>Next:</strong> {prospect.ai_score_next_action}</p>
          )}
        </div>
        <button onClick={onRescore} disabled={rescoring} className="shrink-0 text-[10px] font-bold tracking-widest uppercase px-2 py-1 border border-blue-200 hover:border-blue-500 text-blue-700 disabled:opacity-50 inline-flex items-center gap-1" data-testid="ai-score-rescore">
          {rescoring ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} Re-score
        </button>
      </div>
      {scoredAt && <p className="text-[10px] text-gray-500 mt-2">Scored {scoredAt.toLocaleString()}</p>}
    </div>
  );
}

function OutreachDraftsPanel({ prospect, draftTone, setDraftTone, onRegenerate, drafting }) {
  const d = prospect.outreach_drafts || {};
  const draftedAt = prospect.outreach_drafts_at ? new Date(prospect.outreach_drafts_at) : null;
  const copyOne = (label, text) => { navigator.clipboard?.writeText(text); toast.success(`${label} copied`); };
  const copyAll = () => {
    const text = `📧 EMAIL\nSubject: ${d.email_subject || ''}\n\n${d.email_body || ''}\n\n— — —\n\n💼 LINKEDIN DM\n${d.linkedin_dm || ''}\n\n— — —\n\n📱 SMS\n${d.sms || ''}`;
    navigator.clipboard?.writeText(text);
    toast.success('All drafts copied');
  };
  return (
    <div className="p-3 border border-emerald-200 bg-emerald-50/30" data-testid="outreach-drafts-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <p className="text-[10px] font-bold tracking-widest uppercase text-emerald-900 flex items-center gap-1.5">
          <Send size={11} /> READY-TO-SEND OUTREACH · {(prospect.outreach_tone || draftTone || 'direct').toUpperCase()}
        </p>
        <div className="flex items-center gap-1.5">
          {['direct', 'warm', 'punchy'].map((t) => (
            <button key={t} onClick={() => { setDraftTone(t); onRegenerate(t); }} disabled={drafting}
              className={`text-[10px] font-bold tracking-widest uppercase px-2 py-1 border transition-colors ${(prospect.outreach_tone || draftTone) === t ? 'bg-emerald-700 text-white border-emerald-700' : 'border-emerald-200 text-emerald-700 hover:bg-emerald-100'} disabled:opacity-50`}
              data-testid={`outreach-tone-${t}`}>{t.toUpperCase()}</button>
          ))}
          <button onClick={copyAll} className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border border-emerald-300 hover:bg-emerald-100 text-emerald-800 inline-flex items-center gap-1" data-testid="outreach-copy-all">
            <Copy size={10} /> Copy all
          </button>
        </div>
      </div>
      <div className="space-y-2.5">
        {d.email_subject && (
          <div className="bg-white border border-emerald-100" data-testid="outreach-email">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-700 inline-flex items-center gap-1.5"><Mail size={11} /> EMAIL</p>
              <button onClick={() => copyOne('Email', `Subject: ${d.email_subject}\n\n${d.email_body}`)} className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border border-gray-200 hover:border-gray-900 text-gray-700">COPY</button>
            </div>
            <div className="px-3 py-2.5">
              <p className="text-[11px] text-gray-500 mb-1">Subject:</p>
              <p className="text-sm font-bold text-gray-900 mb-2">{d.email_subject}</p>
              <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">{d.email_body}</p>
            </div>
          </div>
        )}
        {d.linkedin_dm && (
          <div className="bg-white border border-emerald-100" data-testid="outreach-linkedin">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-700 inline-flex items-center gap-1.5"><Link2 size={11} /> LINKEDIN DM</p>
              <button onClick={() => copyOne('LinkedIn DM', d.linkedin_dm)} className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border border-gray-200 hover:border-gray-900 text-gray-700">COPY</button>
            </div>
            <p className="px-3 py-2.5 text-xs text-gray-700 leading-relaxed">{d.linkedin_dm}</p>
          </div>
        )}
        {d.sms && (
          <div className="bg-white border border-emerald-100" data-testid="outreach-sms">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 bg-gray-50">
              <p className="text-[10px] font-bold tracking-widest uppercase text-gray-700 inline-flex items-center gap-1.5"><Phone size={11} /> SMS</p>
              <button onClick={() => copyOne('SMS', d.sms)} className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 border border-gray-200 hover:border-gray-900 text-gray-700">COPY</button>
            </div>
            <p className="px-3 py-2.5 text-xs text-gray-700 leading-relaxed">{d.sms}</p>
          </div>
        )}
      </div>
      {draftedAt && <p className="text-[10px] text-gray-500 mt-2">Drafted {draftedAt.toLocaleString()}. Switch tone to regenerate.</p>}
    </div>
  );
}

function ConflictsPanel({ prospect }) {
  const conflicts = prospect.conflicts || [];
  const detectedAt = prospect.conflicts_detected_at ? new Date(prospect.conflicts_detected_at) : null;
  const copyAll = () => {
    const text = conflicts.map((c, i) => `${i + 1}. ${c.title || c.type}\n\nWhat: ${c.what || ''}\nWhy it matters: ${c.why || ''}\nWhat we do: ${c.fix || ''}\n\nPitch line: ${c.pitch_line}`).join('\n\n— — —\n\n');
    navigator.clipboard?.writeText(text);
    toast.success('Full breakdown copied to clipboard');
  };
  const SEVERITY = {
    high:   { color: '#B91C1C', bg: '#FEF2F2', border: '#FECACA', label: 'CRITICAL', dot: '#DC2626' },
    medium: { color: '#92400E', bg: '#FEF3C7', border: '#FDE68A', label: 'NEEDS ATTENTION', dot: '#D97706' },
    low:    { color: '#374151', bg: '#F3F4F6', border: '#E5E7EB', label: 'MINOR', dot: '#6B7280' },
  };
  return (
    <div className="p-4 border border-amber-200 bg-amber-50/30" data-testid="conflicts-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] font-bold tracking-widest uppercase text-amber-900 flex items-center gap-1.5">
          <ShieldAlert size={12} /> ISSUES WE FOUND · {conflicts.length}
        </p>
        <button onClick={copyAll} className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 border border-amber-300 hover:bg-amber-100 text-amber-800 inline-flex items-center gap-1" data-testid="conflicts-copy-all">
          <Copy size={10} /> Copy full breakdown
        </button>
      </div>
      <p className="text-[11px] text-amber-800 mb-3 leading-snug">Each issue below is written so any teammate can read it out loud. Hand it to your sales rep — they have a complete story for the prospect.</p>
      <div className="space-y-3">
        {conflicts.map((c, i) => {
          const sc = SEVERITY[c.severity] || SEVERITY.low;
          return (
            <div key={i} className="bg-white border border-amber-100" data-testid={`conflict-${i}`}>
              {/* Header */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-gray-100 bg-gray-50">
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: sc.dot }} />
                <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 border" style={{ color: sc.color, background: sc.bg, borderColor: sc.border }}>{sc.label}</span>
                <span className="text-sm font-bold text-gray-900 flex-1">{c.title || (c.type || '').replace(/_/g, ' ')}</span>
              </div>
              {/* Body — 3-step explanation */}
              <div className="p-3 space-y-2.5">
                {c.what && (
                  <div className="grid grid-cols-[80px,1fr] gap-2 items-start">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mt-0.5">WHAT</span>
                    <p className="text-xs text-gray-800 leading-relaxed">{c.what}</p>
                  </div>
                )}
                {c.why && (
                  <div className="grid grid-cols-[80px,1fr] gap-2 items-start">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mt-0.5">WHY IT HURTS</span>
                    <p className="text-xs text-gray-800 leading-relaxed">{c.why}</p>
                  </div>
                )}
                {c.fix && (
                  <div className="grid grid-cols-[80px,1fr] gap-2 items-start">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-emerald-700 mt-0.5">OUR FIX</span>
                    <p className="text-xs text-gray-800 leading-relaxed">{c.fix}</p>
                  </div>
                )}
                {(c.value_a || c.value_b) && (
                  <div className="grid grid-cols-[80px,1fr] gap-2 items-start pt-2 border-t border-gray-100">
                    <span className="text-[10px] font-bold tracking-widest uppercase text-gray-400 mt-0.5">SOURCES</span>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-gray-600">
                      {c.value_a && <span><strong className="text-gray-800">{c.source_a}:</strong> {c.value_a}</span>}
                      {c.value_b && <span><strong className="text-gray-800">{c.source_b}:</strong> {c.value_b}</span>}
                    </div>
                  </div>
                )}
                {c.pitch_line && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 text-xs text-amber-900 italic leading-snug">
                    <span className="text-[9px] font-bold tracking-widest uppercase text-amber-700 not-italic mr-1.5">PITCH LINE →</span>
                    "{c.pitch_line}"
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {detectedAt && (
        <p className="text-[10px] text-gray-500 mt-3">Last checked {detectedAt.toLocaleString()}. Use "Re-check" to pull fresh Google + Yelp data anytime.</p>
      )}
    </div>
  );
}


function PublicDemoPanel({ prospect, onRegenerate, regenerating }) {  const slug = prospect.demo_slug || prospect.demo_token;
  const path = slug ? `/demo/${slug}` : '';
  const fullUrl = path ? `${window.location.origin}${path}` : '';
  const views = prospect.demo_views || 0;
  const captures = (prospect.demo_captures || []).length;
  const lastViewed = prospect.demo_last_viewed_at
    ? new Date(prospect.demo_last_viewed_at).toLocaleString()
    : null;

  const copy = () => {
    navigator.clipboard?.writeText(fullUrl);
    toast.success('Public demo URL copied');
  };

  return (
    <div className="p-3 border border-gray-200 bg-[#FAFAFA]" data-testid="public-demo-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 flex items-center gap-1.5">
          <Link2 size={11} /> PUBLIC DEMO PAGE
        </p>
        {views > 0 && (
          <span className="text-[10px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-[#FF2E00] text-white inline-flex items-center gap-1" data-testid="public-demo-views">
            <Eye size={10} /> {views} VIEW{views === 1 ? '' : 'S'} · {captures} CAPTURE{captures === 1 ? '' : 'S'}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mb-2">
        <a href={path} target="_blank" rel="noreferrer" className="flex-1 text-xs text-[#FF2E00] hover:underline truncate" data-testid="public-demo-url">
          {fullUrl}
        </a>
        <button onClick={copy} className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border border-gray-300 hover:border-gray-900 text-gray-700" data-testid="public-demo-copy">COPY</button>
      </div>
      {lastViewed && <p className="text-[10px] text-gray-500 mb-2">Last viewed: {lastViewed}</p>}
      {(prospect.demo_ai_insights || []).length > 0 && (
        <details className="text-xs text-gray-700">
          <summary className="cursor-pointer text-[10px] font-bold tracking-widest uppercase text-gray-500 hover:text-gray-900">AI INSIGHTS ({(prospect.demo_ai_insights || []).length})</summary>
          <ol className="list-decimal pl-5 mt-2 space-y-1">
            {(prospect.demo_ai_insights || []).map((it, i) => <li key={i} className="text-xs">{it}</li>)}
          </ol>
          <button onClick={onRegenerate} disabled={regenerating} className="mt-2 text-[10px] font-bold tracking-widest uppercase text-gray-500 hover:text-gray-900 inline-flex items-center gap-1 disabled:opacity-50" data-testid="public-demo-regen-insights">
            {regenerating ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />} REGENERATE
          </button>
        </details>
      )}
    </div>
  );
}


/* ── Proposal Tab ────────────────────────────────────────────── */
function ProposalTab({ prospect, onSaved }) {
  const existing = prospect.last_proposal;
  const [pkg, setPkg] = useState(existing?.package || 'launch');
  const [monthly, setMonthly] = useState(existing?.monthly_price ?? '');
  const [setup, setSetup] = useState(existing?.setup_fee ?? '');
  const [timeline, setTimeline] = useState(existing?.timeline || '');
  const [pain, setPain] = useState(existing?.pain_points || '');
  const [solution, setSolution] = useState(existing?.recommended_solution || '');
  const [whatBuild, setWhatBuild] = useState(existing?.what_we_build || '');
  const [impact, setImpact] = useState(existing?.expected_impact || '');
  const [nextSteps, setNextSteps] = useState(existing?.next_steps || '');
  const [generated, setGenerated] = useState(existing || null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const body = {
        package: pkg,
        monthly_price: monthly === '' ? null : Number(monthly),
        setup_fee: setup === '' ? null : Number(setup),
        timeline, pain_points: pain, recommended_solution: solution,
        what_we_build: whatBuild, expected_impact: impact, next_steps: nextSteps,
      };
      const r = await authFetch(`/api/staff/prospects/${prospect.id}/proposal`, { method: 'POST', body: JSON.stringify(body) });
      setGenerated(r);
      toast.success('Proposal generated');
      if (onSaved) onSaved();
    } catch (err) { toast.error(err.message || 'Failed to generate'); }
    finally { setBusy(false); }
  };

  const copyText = () => {
    if (!generated?.text) return;
    navigator.clipboard.writeText(generated.text).then(() => toast.success('Copied to clipboard'));
  };

  return (
    <div className="p-6 space-y-4" data-testid="proposal-tab">
      <div className="grid grid-cols-3 gap-2">
        {['launch', 'grow', 'dominate'].map((k) => (
          <button key={k} onClick={() => setPkg(k)}
            className={`text-xs font-bold tracking-widest uppercase py-2 border transition-colors ${
              pkg === k ? 'bg-gray-900 border-gray-900 text-white' : 'border-gray-200 text-gray-700 hover:border-gray-900'
            }`} data-testid={`prop-pkg-${k}`}>{k}</button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Monthly $" type="number" value={monthly} onChange={setMonthly} testId="prop-monthly" />
        <Field label="Setup $" type="number" value={setup} onChange={setSetup} testId="prop-setup" />
      </div>
      <Field label="Timeline" value={timeline} onChange={setTimeline} testId="prop-timeline" />

      <details className="border border-gray-200 p-3">
        <summary className="text-[10px] font-bold tracking-widest uppercase text-gray-700 cursor-pointer">Customize copy (optional)</summary>
        <div className="space-y-2 mt-3">
          <TextArea label="Pain points" value={pain} onChange={setPain} testId="prop-pain" />
          <TextArea label="Recommended solution" value={solution} onChange={setSolution} testId="prop-solution" />
          <TextArea label="What we'll build" value={whatBuild} onChange={setWhatBuild} testId="prop-build" />
          <TextArea label="Expected impact" value={impact} onChange={setImpact} testId="prop-impact" />
          <TextArea label="Next steps" value={nextSteps} onChange={setNextSteps} testId="prop-next" />
        </div>
      </details>

      <button onClick={generate} disabled={busy}
        className="w-full py-3 text-xs font-bold tracking-widest uppercase bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50"
        data-testid="prop-generate">
        {busy ? 'GENERATING…' : (generated ? 'REGENERATE' : 'GENERATE PROPOSAL')}
      </button>

      {generated && (
        <div className="space-y-2" data-testid="prop-output">
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500">Generated proposal</p>
            <button onClick={copyText} className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 border border-gray-200 hover:border-gray-900" data-testid="prop-copy">
              COPY TEXT
            </button>
          </div>
          <div className="bg-gray-50 border border-gray-200 p-4 max-h-96 overflow-y-auto" dangerouslySetInnerHTML={{ __html: generated.html }} />
          <details className="text-xs text-gray-600">
            <summary className="cursor-pointer">Plain-text version</summary>
            <pre className="whitespace-pre-wrap font-sans bg-gray-50 border border-gray-200 p-3 mt-2 leading-relaxed">{generated.text}</pre>
          </details>
        </div>
      )}
    </div>
  );
}

function TextArea({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-1">{label}</label>
      <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={3}
        className="w-full px-3 py-2 text-sm bg-white border border-gray-200 focus:outline-none focus:border-gray-900"
        data-testid={testId} />
    </div>
  );
}

/* ── Reports Modal ───────────────────────────────────────────── */
function ReportsModal({ onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { authFetch('/api/staff/prospects/reports').then(setData).catch((e) => toast.error(e.message)); }, []);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="reports-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-5xl bg-white shadow-2xl my-auto" style={{ fontFamily: 'Manrope' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.5px', color: '#0F0F0F' }}>
            REPORTS · NATIONAL PROSPECTING
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1" data-testid="reports-close"><XIcon size={20} /></button>
        </div>
        <div className="p-6 space-y-6">
          {!data && <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin text-gray-400" /></div>}
          {data && <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <StatCard label="Total prospects" value={data.totals.all} testId="rpt-total" />
              <StatCard label="Last 7 days" value={data.totals.last_7_days} accent="#FF2E00" testId="rpt-7d" />
              <StatCard label="Active pipeline" value={data.pipeline.active_count} testId="rpt-active" />
              <StatCard label="Est. ARR" value={`$${(data.pipeline.estimated_arr_value / 1000).toFixed(0)}K`} accent="#10B981" testId="rpt-arr" />
            </div>

            <Section title="Funnel">
              <div className="grid grid-cols-3 md:grid-cols-9 gap-1">
                {Object.entries(data.funnel).map(([k, v]) => (
                  <div key={k} className="bg-gray-50 border border-gray-200 px-2 py-2 text-center" data-testid={`rpt-funnel-${k}`}>
                    <p className="text-[9px] font-bold tracking-wide uppercase text-gray-500 truncate">{k.replace(/_/g, ' ')}</p>
                    <p className="text-lg font-bold tabular-nums">{v}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">Win rate (closed_won / contacted): <strong>{data.pipeline.win_rate_pct}%</strong></p>
            </Section>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Section title="Top states">
                <BarList items={data.by_state.map((r) => ({ key: r.state, label: r.state, count: r.count, won: r.won }))} />
              </Section>
              <Section title="Top cities">
                <BarList items={data.by_city.map((r) => ({ key: `${r.city}-${r.state}`, label: `${r.city}, ${r.state}`, count: r.count, won: r.won }))} />
              </Section>
              <Section title="By gym category">
                <BarList items={data.by_category.map((r) => ({ key: r.category, label: r.category.replace(/_/g, ' '), count: r.count, won: r.won }))} />
              </Section>
              <Section title="By opportunity tag">
                <BarList items={data.by_tag.map((r) => ({ key: r.tag, label: r.tag.replace(/_/g, ' '), count: r.count, won: r.won }))} />
              </Section>
            </div>

            <Section title="Outreach performance">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                <StatCard label="Sent total" value={data.outreach.sent_total} testId="rpt-sent-total" />
                <StatCard label="Sent 7d" value={data.outreach.sent_7d} testId="rpt-sent-7d" />
                <StatCard label="Replied" value={data.outreach.replied} accent="#FF2E00" testId="rpt-replied" />
                <StatCard label="Reply rate" value={`${data.outreach.reply_rate_pct}%`} accent="#10B981" testId="rpt-reply-rate" />
              </div>
              <div className="space-y-1">
                {data.outreach.by_sequence.map((s) => (
                  <div key={s.sequence} className="flex items-center justify-between bg-gray-50 border border-gray-200 px-3 py-2 text-sm" data-testid={`rpt-seq-${s.sequence}`}>
                    <span className="font-bold">{s.sequence.replace(/_/g, ' ')}</span>
                    <span className="text-gray-600 text-xs">{s.sent} sent · {s.replied} replied · {s.reply_rate_pct}%</span>
                  </div>
                ))}
                {data.outreach.by_sequence.length === 0 && <p className="text-xs text-gray-500">No outreach sent yet.</p>}
              </div>
            </Section>
          </>}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="text-[11px] font-bold tracking-widest uppercase text-gray-500 mb-2">{title}</h3>
      {children}
    </div>
  );
}

function BarList({ items }) {
  if (!items || items.length === 0) return <p className="text-xs text-gray-500">No data yet.</p>;
  const max = Math.max(...items.map((i) => i.count), 1);
  return (
    <div className="space-y-1.5">
      {items.slice(0, 8).map((it) => (
        <div key={it.key} className="text-xs">
          <div className="flex items-center justify-between mb-0.5">
            <span className="text-gray-700 capitalize">{it.label}</span>
            <span className="text-gray-500 tabular-nums">{it.count}{it.won ? ` · ${it.won} won` : ''}</span>
          </div>
          <div className="w-full h-1.5 bg-gray-100 overflow-hidden">
            <div className="h-full bg-[#FF2E00]" style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── Main Page ───────────────────────────────────────────────── */
export default function Prospects() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ stage: '', state: '', city: '', category: '', tag: '', min_score: '', search: '' });
  const [selected, setSelected] = useState(new Set());
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detailFull, setDetailFull] = useState(null);
  const fileRef = useRef(null);
  const [showReports, setShowReports] = useState(false);
  const [showDiscover, setShowDiscover] = useState(false);

  const scanBulk = async () => {
    try {
      const ids = selected.size > 0 ? [...selected] : null;
      const r = await authFetch('/api/staff/prospects/scan-bulk', { method: 'POST', body: JSON.stringify({ ids }) });
      toast.success(`Scanned ${r.scanned} site${r.scanned === 1 ? '' : 's'}`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const enrichBulk = async () => {
    try {
      const ids = selected.size > 0 ? [...selected] : null;
      toast.message('Enriching… IG bios + Google ratings (free tier).');
      const r = await authFetch('/api/staff/prospects/enrich-bulk', { method: 'POST', body: JSON.stringify({ ids }) });
      toast.success(`Enriched ${r.enriched} prospect${r.enriched === 1 ? '' : 's'}`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const [refreshingPaid, setRefreshingPaid] = useState(false);
  const refreshPaid = async () => {
    if (!window.confirm('Refresh Google Places + Yelp data on missing prospects (up to 50)?\nUses your API quotas — small batch, totally safe.')) return;
    setRefreshingPaid(true);
    try {
      const ids = selected.size > 0 ? [...selected] : null;
      const r = await authFetch('/api/staff/prospects/refresh-paid-bulk', {
        method: 'POST',
        body: JSON.stringify({ limit: ids ? ids.length : 50, ids, only_missing: !ids }),
      });
      toast.success(`Refreshed ${r.processed} prospects — Google +${r.enriched_google}, Yelp +${r.enriched_yelp}`);
      load();
    } catch (err) { toast.error(err.message || 'Refresh failed'); }
    finally { setRefreshingPaid(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const [list, s, seq] = await Promise.all([
        authFetch(`/api/staff/prospects?${params.toString()}`),
        authFetch('/api/staff/prospects/stats'),
        authFetch('/api/staff/prospects/sequences'),
      ]);
      setItems(list.items || []);
      setStats(s);
      setSequences(seq.sequences || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load prospects');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // Load full detail when one row is selected for the drawer
  useEffect(() => {
    if (!detailId) { setDetailFull(null); return; }
    authFetch(`/api/staff/prospects/${detailId}`)
      .then(setDetailFull)
      .catch((err) => toast.error(err.message));
  }, [detailId]);

  const allSelected = items.length > 0 && selected.size === items.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(items.map((i) => i.id)));
  const toggleOne = (id) => setSelected((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const bulk = async (action, extra = {}) => {
    if (selected.size === 0) { toast.error('Select rows first'); return; }
    try {
      const r = await authFetch('/api/staff/prospects/bulk-action', {
        method: 'POST',
        body: JSON.stringify({ ids: [...selected], action, ...extra }),
      });
      toast.success(`Updated ${r.modified} row(s)`);
      setSelected(new Set());
      load();
    } catch (err) { toast.error(err.message); }
  };

  const importCsv = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    const token = localStorage.getItem('scs_token') || localStorage.getItem('huscleos_token');
    try {
      const res = await fetch(`${API}/api/staff/prospects/import-csv`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'import failed');
      toast.success(`Imported ${d.imported}, skipped ${d.skipped}`);
      load();
    } catch (err) { toast.error(err.message); }
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem('scs_token') || localStorage.getItem('huscleos_token');
    const res = await fetch(`${API}/api/staff/prospects/template/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'huscleos-prospects-template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const filterCount = useMemo(() => Object.values(filters).filter(Boolean).length, [filters]);

  return (
    <div className="min-h-screen bg-gray-50" style={{ fontFamily: 'Manrope' }}>
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/staff/huscleos" className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900">
              <ArrowLeft size={12} /> Sales
            </Link>
            <span className="text-gray-300">/</span>
            <div className="flex items-center gap-1">
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.2rem', color: '#0F0F0F' }}>HUSCLE</span>
              <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.2rem', color: '#FF2E00' }}>OS</span>
              <span className="text-xs font-bold tracking-widest uppercase text-gray-400 ml-2">PROSPECTS</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={loading} className="p-2 text-gray-500 hover:text-gray-900" data-testid="prospects-refresh">
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>
            <button onClick={() => setShowDiscover(true)} className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border-2 border-[#FF2E00] text-[#FF2E00] hover:bg-[#FF2E00] hover:text-white transition-colors" data-testid="prospects-discover">
              <Compass size={12} /> Discover
            </button>
            <button onClick={scanBulk} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900" data-testid="prospects-scan-bulk">
              <Zap size={12} /> Scan
            </button>
            <button onClick={enrichBulk} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900" data-testid="prospects-enrich-bulk" title="Free IG bio + Google rating lookup">
              <Sparkles size={12} /> Enrich
            </button>
            <button onClick={refreshPaid} disabled={refreshingPaid} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-900 bg-gray-900 text-white hover:bg-black disabled:opacity-50" data-testid="prospects-refresh-paid" title="Google Places + Yelp Fusion backfill on missing prospects">
              {refreshingPaid ? <Loader2 size={12} className="animate-spin" /> : <Flame size={12} />} {refreshingPaid ? 'Refreshing…' : 'Refresh+'}
            </button>
            <button onClick={() => setShowReports(true)} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900" data-testid="prospects-reports">
              Reports
            </button>
            <button onClick={downloadTemplate} className="hidden sm:inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900" data-testid="prospects-template">
              <Download size={12} /> Template
            </button>
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
            <button onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 border border-gray-200 text-gray-700 hover:border-gray-900 hover:text-gray-900" data-testid="prospects-import">
              <Upload size={12} /> Import
            </button>
            <button onClick={() => { setEditing(null); setOpenForm(true); }} className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1.5 bg-[#FF2E00] text-white hover:bg-[#E02900]" data-testid="prospects-add">
              <Plus size={12} /> Add
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-5">
        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
            <StatCard label="Total" value={stats.total} testId="kpi-total" />
            <StatCard label="Needs Review" value={stats.needs_review} accent="#9CA3AF" testId="kpi-review" />
            <StatCard label="Approved" value={stats.approved} accent="#0F0F0F" testId="kpi-approved" />
            <StatCard label="Contacted" value={stats.contacted} accent="#1F2937" testId="kpi-contacted" />
            <StatCard label="No-Website" value={stats.no_website_count} accent="#FF2E00" testId="kpi-no-website" />
            <StatCard label="Sent Today" value={stats.emails_sent_today} accent="#FF2E00" testId="kpi-sent-today" />
            <StatCard label="Closed Won" value={stats.closed_won} accent="#10B981" testId="kpi-closed-won" />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white border border-gray-200 p-3 mb-3 flex flex-wrap items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          <span className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mr-2">Filter</span>

          <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 px-2.5 py-1.5">
            <Search size={12} className="text-gray-400" />
            <input value={filters.search} onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              placeholder="Search gym, city, email…" className="bg-transparent text-sm focus:outline-none w-44" data-testid="filter-search" />
          </div>
          <Select value={filters.stage} onChange={(v) => setFilters({ ...filters, stage: v })} placeholder="All stages"
            options={STAGES.map((s) => ({ value: s.key, label: s.label }))} testId="filter-stage" />
          <Select value={filters.category} onChange={(v) => setFilters({ ...filters, category: v })} placeholder="Category"
            options={CATEGORIES.map((c) => ({ value: c, label: c.replace(/_/g, ' ') }))} testId="filter-category" />
          <Select value={filters.tag} onChange={(v) => setFilters({ ...filters, tag: v })} placeholder="Tag"
            options={TAGS.map((t) => ({ value: t, label: tagLabel(t) }))} testId="filter-tag" />
          <input value={filters.state} onChange={(e) => setFilters({ ...filters, state: e.target.value })}
            placeholder="State" className="bg-gray-50 border border-gray-200 px-2 py-1.5 text-sm w-20 focus:outline-none focus:border-gray-900" data-testid="filter-state" />
          <input value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            placeholder="City" className="bg-gray-50 border border-gray-200 px-2 py-1.5 text-sm w-28 focus:outline-none focus:border-gray-900" data-testid="filter-city" />
          <input value={filters.min_score} onChange={(e) => setFilters({ ...filters, min_score: e.target.value })}
            type="number" placeholder="Min score" className="bg-gray-50 border border-gray-200 px-2 py-1.5 text-sm w-24 focus:outline-none focus:border-gray-900" data-testid="filter-score" />
          {filterCount > 0 && (
            <button onClick={() => setFilters({ stage: '', state: '', city: '', category: '', tag: '', min_score: '', search: '' })}
              className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 text-gray-500 hover:text-gray-900" data-testid="filter-clear">
              CLEAR ({filterCount})
            </button>
          )}
        </div>

        {/* Bulk actions */}
        {selected.size > 0 && (
          <div className="bg-gray-900 text-white px-3 py-2 mb-3 flex items-center gap-2 flex-wrap" data-testid="bulk-bar">
            <span className="text-xs font-bold tracking-widest uppercase">{selected.size} selected</span>
            <button onClick={() => bulk('approve')} className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 bg-[#FF2E00] hover:bg-[#E02900]" data-testid="bulk-approve">
              <CheckCircle2 size={11} className="inline mr-1" /> Approve
            </button>
            <button onClick={() => bulk('reject')} className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 border border-white/30 hover:bg-white/10" data-testid="bulk-reject">
              <XCircle size={11} className="inline mr-1" /> Reject
            </button>
            <button onClick={() => bulk('do_not_contact')} className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 border border-white/30 hover:bg-white/10" data-testid="bulk-dnc">
              DNC
            </button>
            <button onClick={() => setSelected(new Set())} className="ml-auto text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100">CLEAR</button>
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="prospects-table">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2.5 w-8"><input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="select-all" /></th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500">Gym</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500 hidden md:table-cell">Location</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500 hidden lg:table-cell">Tags</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500">Stage</th>
                <th className="px-3 py-2.5 text-right text-[10px] font-bold tracking-widest uppercase text-gray-500 w-16">Score</th>
                <th className="px-3 py-2.5 text-left text-[10px] font-bold tracking-widest uppercase text-gray-500 hidden sm:table-cell">Contact</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 inline" /></td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16 text-sm text-gray-500">
                  No prospects yet. Click <strong>Add</strong> or <strong>Import</strong> to start your pipeline.
                </td></tr>
              )}
              {items.map((p) => {
                const stage = STAGES.find((s) => s.key === p.stage) || STAGES[0];
                return (
                  <tr key={p.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setDetailId(p.id)} data-testid={`prospect-row-${p.id}`}>
                    <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleOne(p.id)} data-testid={`select-${p.id}`} />
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-gray-900 leading-tight">{p.gym_name}</p>
                        {(p.conflicts_count || 0) > 0 && (
                          <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-0.5" title={`${p.conflicts_count} pitch-ready conflicts detected`} data-testid={`row-conflicts-${p.id}`}>
                            <ShieldAlert size={9} /> {p.conflicts_count}
                          </span>
                        )}
                        {p.ai_score != null && (
                          <span
                            className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 border inline-flex items-center gap-0.5"
                            style={{
                              color: p.ai_score >= 80 ? '#065F46' : p.ai_score >= 60 ? '#92400E' : '#4B5563',
                              background: p.ai_score >= 80 ? '#ECFDF5' : p.ai_score >= 60 ? '#FEF3C7' : '#F3F4F6',
                              borderColor: p.ai_score >= 80 ? '#86EFAC' : p.ai_score >= 60 ? '#FDE68A' : '#E5E7EB',
                            }}
                            title={p.ai_score_reasoning}
                            data-testid={`row-ai-score-${p.id}`}
                          >
                            <Sparkles size={9} /> {p.ai_score}
                          </span>
                        )}
                        {p.is_stale && (
                          <span className="text-[9px] font-bold tracking-widest uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 inline-flex items-center gap-0.5" title={p.stale_reason || 'Stale lead'} data-testid={`row-stale-${p.id}`}>
                            <Clock size={9} /> STALE
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 leading-tight">{p.category?.replace(/_/g, ' ')} · {p.email || 'no email'}</p>
                      <SocialIcons prospect={p} className="mt-1.5" />
                    </td>
                    <td className="px-3 py-2.5 hidden md:table-cell text-xs text-gray-700">
                      {[p.city, p.state].filter(Boolean).join(', ') || '—'}
                    </td>
                    <td className="px-3 py-2.5 hidden lg:table-cell">
                      <div className="flex flex-wrap gap-1">
                        {(p.opportunity_tags || []).slice(0, 2).map((t) => (
                          <span key={t} className="text-[9px] font-semibold tracking-wide uppercase px-1.5 py-0.5 bg-gray-100 text-gray-600">{tagLabel(t)}</span>
                        ))}
                        {(p.opportunity_tags || []).length > 2 && <span className="text-[9px] text-gray-400">+{p.opportunity_tags.length - 2}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-1 inline-block" style={{ background: stage.bg, color: stage.color }}>
                        {stage.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums">
                      <span className={`text-sm font-bold ${p.fit_score >= 75 ? 'text-[#FF2E00]' : p.fit_score >= 60 ? 'text-gray-900' : 'text-gray-500'}`}>{p.fit_score}</span>
                    </td>
                    <td className="px-3 py-2.5 hidden sm:table-cell text-xs text-gray-700">
                      {p.contact_name || p.owner_name || '—'}
                      {p.phone && <span className="text-gray-500 block text-[11px]">{p.phone}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          {items.length} prospects · sorted by fit score · click a row to open
        </p>
      </div>

      {openForm && (
        <ProspectFormModal
          prospect={editing}
          onClose={() => { setOpenForm(false); setEditing(null); }}
          onSaved={() => load()}
        />
      )}

      {detailId && detailFull && (
        <ProspectDetail
          prospect={detailFull}
          sequences={sequences}
          onClose={() => { setDetailId(null); setDetailFull(null); }}
          onChanged={() => {
            load();
            authFetch(`/api/staff/prospects/${detailId}`).then(setDetailFull).catch(() => {});
          }}
        />
      )}

      {showReports && <ReportsModal onClose={() => setShowReports(false)} />}
      {showDiscover && <DiscoverModal onClose={() => setShowDiscover(false)} onDiscovered={load} />}
    </div>
  );
}

/* ── Discover Modal ──────────────────────────────────────────── */
function DiscoverModal({ onClose, onDiscovered }) {
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [keyword, setKeyword] = useState('');
  const [websiteFilter, setWebsiteFilter] = useState('any'); // any | with | without
  const [useOSM, setUseOSM] = useState(true);
  const [usePlaces, setUsePlaces] = useState(true);
  const [useYelp, setUseYelp] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!city && !state) { toast.error('City or state required'); return; }
    setBusy(true);
    try {
      const r = await authFetch('/api/staff/prospects/discover', {
        method: 'POST',
        body: JSON.stringify({
          city, state, keyword,
          sources: { osm: useOSM, places: usePlaces, yelp: useYelp },
          website_filter: websiteFilter,
          auto_save: true,
        }),
      });
      setResult(r);
      if (r.status === 'no_sources') {
        toast.error(r.message || 'No sources available');
      } else {
        toast.success(`Saved ${r.saved} new · ${r.duplicates} dupes skipped`);
        if (r.saved > 0 && onDiscovered) onDiscovered();
      }
    } catch (err) { toast.error(err.message || 'Discovery failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4 py-8 bg-black/70 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-testid="discover-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-xl bg-white shadow-2xl my-auto" style={{ fontFamily: 'Manrope' }}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Bebas Neue', letterSpacing: '0.5px', color: '#0F0F0F' }}>
            DISCOVER · GYMS NATIONWIDE
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900 p-1" data-testid="discover-close"><XIcon size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">Pulls live from <strong>OpenStreetMap (free)</strong>, Google Places, and Yelp. Dedupes against your pipeline. Saves new gyms in <strong>Needs Review</strong>.</p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="City" value={city} onChange={setCity} testId="discover-city" />
            <Field label="State (e.g. CO)" value={state} onChange={setState} testId="discover-state" />
          </div>
          <Field label="Keyword (optional — e.g. crossfit, powerlifting, boxing)" value={keyword} onChange={setKeyword} testId="discover-keyword" />

          <div>
            <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Website filter</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { v: 'any',     l: 'ANY',    h: 'with or without' },
                { v: 'without', l: 'NO SITE', h: 'best for cold pitch' },
                { v: 'with',    l: 'HAS SITE', h: 'we can scan their funnel' },
              ].map((opt) => (
                <button key={opt.v} onClick={() => setWebsiteFilter(opt.v)}
                  className={`text-xs font-bold tracking-widest uppercase px-2 py-2.5 border transition-colors ${
                    websiteFilter === opt.v ? 'bg-[#FF2E00] border-[#FF2E00] text-white' : 'border-gray-200 text-gray-700 hover:border-gray-900'
                  }`} data-testid={`discover-web-${opt.v}`}>
                  {opt.l}
                  <span className="block text-[9px] font-medium tracking-normal normal-case mt-0.5 opacity-80">{opt.h}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">Sources</label>
            <div className="space-y-1.5 text-sm text-gray-700">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={useOSM} onChange={(e) => setUseOSM(e.target.checked)} data-testid="discover-osm" />
                OpenStreetMap (free, no API key — recommended)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={usePlaces} onChange={(e) => setUsePlaces(e.target.checked)} data-testid="discover-places" />
                Google Places (only if API key configured)
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={useYelp} onChange={(e) => setUseYelp(e.target.checked)} data-testid="discover-yelp" />
                Yelp Fusion (only if API key configured)
              </label>
            </div>
          </div>

          <button onClick={run} disabled={busy} className="w-full py-3 text-xs font-bold tracking-widest uppercase bg-[#FF2E00] text-white hover:bg-[#E02900] disabled:opacity-50 flex items-center justify-center gap-2" data-testid="discover-submit">
            {busy ? <><Loader2 size={14} className="animate-spin" /> SEARCHING (10–30s for OSM)…</> : <><Compass size={13} /> RUN DISCOVERY</>}
          </button>

          {result && (
            <div className="mt-2 p-3 bg-gray-50 border border-gray-200 text-sm" data-testid="discover-result">
              {result.status === 'no_sources' ? (
                <p className="text-yellow-800 text-xs">{result.message}</p>
              ) : (
                <>
                  <p className="font-bold text-gray-900">Discovered {result.discovered} · Saved {result.saved} · Duplicates {result.duplicates}</p>
                  <p className="text-xs text-gray-500 mt-1 mb-3">
                    {Object.entries(result.sources || {}).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v}`).join(' · ') || 'No sources returned data'}
                  </p>
                  {(result.items || []).length > 0 && (
                    <div className="border-t border-gray-200 pt-3" data-testid="discover-items">
                      <p className="text-[10px] font-bold tracking-widest uppercase text-gray-500 mb-2">FOUND ({result.items.length})</p>
                      <div className="space-y-1.5 max-h-72 overflow-y-auto -mr-1 pr-1">
                        {result.items.slice(0, 25).map((it, i) => (
                          <div key={i} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-gray-100" data-testid={`discover-item-${i}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-semibold text-gray-900 truncate">{it.gym_name || it.name || 'Unnamed'}</p>
                              <p className="text-[10px] text-gray-500 truncate">
                                {[it.city, it.state].filter(Boolean).join(', ') || it.address || ''}
                                {it.lead_source && <span className="ml-1 text-gray-400">· {it.lead_source.replace(/_/g, ' ')}</span>}
                              </p>
                            </div>
                            <SocialIcons prospect={it} />
                          </div>
                        ))}
                        {result.items.length > 25 && (
                          <p className="text-[10px] text-gray-400 italic text-center pt-1">+{result.items.length - 25} more — see prospects table</p>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Select({ value, onChange, options, placeholder, testId }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="appearance-none bg-gray-50 border border-gray-200 pl-2 pr-7 py-1.5 text-sm focus:outline-none focus:border-gray-900"
        data-testid={testId}>
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <ChevronDown size={11} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
  );
}
